import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import api, { apiError } from '../api';
import { copyReaderLink } from '../shareLink';
import DeltaRenderer from './DeltaRenderer';
import RichTextEditor from './RichTextEditor';
import BlockEditor from './BlockEditor';
import './Session.css';

const AUTOSAVE_MS = 30000;

const draftSignature = (content) => JSON.stringify(content || {});

export default function Session() {
  const { roomCode } = useParams();
  const [session, setSession] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [draft, setDraft] = useState({ ops: [{ insert: '\n' }] });
  const [currentBlockId, setCurrentBlockId] = useState(null);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [fontSize, setFontSize] = useState(24);
  const [enterToSave, setEnterToSave] = useState(true);
  const [reverse, setReverse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [shareStatus, setShareStatus] = useState('');
  const editorRef = useRef(null);
  const draftRef = useRef(draft);
  const currentBlockIdRef = useRef(currentBlockId);
  const savedSignatureRef = useRef('');
  const hasLoadedSessionRef = useRef(false);

  useEffect(() => { draftRef.current = draft; }, [draft]);
  useEffect(() => { currentBlockIdRef.current = currentBlockId; }, [currentBlockId]);

  const loadBlocks = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const response = await api.get(`/sessions/${encodeURIComponent(roomCode)}/blocks`);
      setSession(response.data.session);
      setBlocks(response.data.blocks || []);
      hasLoadedSessionRef.current = true;
      if (initial) setError('');
    } catch (requestError) {
      const status = requestError?.response?.status;
      const sessionIsUnavailable = [401, 403, 404, 410].includes(status);
      if (initial || !hasLoadedSessionRef.current || sessionIsUnavailable) {
        setError(apiError(requestError, 'Could not load this writer session.'));
      }
    } finally {
      if (initial) setLoading(false);
    }
  }, [roomCode]);

  useEffect(() => {
    loadBlocks(true);
    const interval = window.setInterval(() => loadBlocks(false), 10000);
    return () => window.clearInterval(interval);
  }, [loadBlocks]);

  const saveDraft = useCallback(async () => {
    const content = draftRef.current;
    const text = content?.ops?.map((op) => typeof op.insert === 'string' ? op.insert : '').join('').trim();
    if (!text) return 'empty';
    if (draftSignature(content) === savedSignatureRef.current) return 'unchanged';
    if (!session?.session_id) return 'failed';
    setSaving(true);
    setError('');
    try {
      const existingId = currentBlockIdRef.current;
      const response = existingId
        ? await api.put(`/blocks/${existingId}`, { content })
        : await api.post('/blocks', { session_id: session.session_id, content });
      savedSignatureRef.current = draftSignature(content);
      if (!existingId) setCurrentBlockId(response.data.block_id);
      await loadBlocks(false);
      return 'saved';
    } catch (requestError) {
      setError(apiError(requestError, 'Could not save this block.'));
      return 'failed';
    } finally {
      setSaving(false);
    }
  }, [loadBlocks, session]);

  useEffect(() => {
    const interval = window.setInterval(() => { saveDraft(); }, AUTOSAVE_MS);
    return () => window.clearInterval(interval);
  }, [saveDraft]);

  const startNewBlock = async () => {
    const result = await saveDraft();
    if (result === 'failed') return;
    editorRef.current?.setContents({ ops: [{ insert: '\n' }] });
    setDraft({ ops: [{ insert: '\n' }] });
    setCurrentBlockId(null);
    savedSignatureRef.current = '';
    editorRef.current?.focus();
  };

  const handleEditorEnter = () => {
    if (!enterToSave || saving) return false;
    startNewBlock();
    return true;
  };

  const saveEditedBlock = async (blockId, content) => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/blocks/${blockId}`, { content });
      setEditingBlockId(null);
      await loadBlocks(false);
    } catch (requestError) {
      setError(apiError(requestError, 'Could not update this block.'));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    setShareStatus('');
    try {
      const result = await copyReaderLink(roomCode);
      setShareStatus(result.copied ? 'Reader link copied. Paste it into WhatsApp.' : `Copy this link: ${result.url}`);
    } catch (requestError) {
      setShareStatus('Could not copy the link. Please copy the link shown below.');
    }
  };

  const orderedBlocks = reverse ? [...blocks].reverse() : blocks;

  return (
    <div className="session-container">
      <header className="session-header page-subheader">
        <h2>Sermon Session <span className="room-code-badge">{roomCode}</span></h2>
        <div className="session-header-actions">
          <button type="button" className="btn btn-primary" onClick={copyLink}>Copy reader link</button>
        </div>
      </header>
      {shareStatus && <div className="share-feedback" role="status">{shareStatus}</div>}
      {shareStatus.startsWith('Reader link') === false && shareStatus.startsWith('Copy this') && <p className="share-url">{shareStatus.replace('Copy this link: ', '')}</p>}
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {loading && <div className="status-panel" role="status">Loading session…</div>}

      {!loading && session && <>
        <section className="editor-panel page-card" aria-labelledby="editor-title">
          <div className="editor-toolbar-row">
            <div className="font-size-controls" aria-label="Editor text size">
              <button type="button" className="btn btn-secondary" onClick={() => setFontSize((size) => Math.max(10, size - 2))} aria-label="Decrease font size">A-</button>
              <button type="button" className="btn btn-secondary" onClick={() => setFontSize((size) => Math.min(32, size + 2))} aria-label="Make editor text larger">A+</button>
            </div>
            <label className="enter-save-toggle" htmlFor="enter-save-toggle">
              <span>Enter saves block</span>
              <input id="enter-save-toggle" type="checkbox" checked={enterToSave} onChange={(event) => setEnterToSave(event.target.checked)} />
            </label>
            <button type="button" className="btn btn-primary" onClick={startNewBlock} disabled={saving}>{saving ? 'Saving...' : 'Save and Start New Block'}</button>
          </div>
          <h3 id="editor-title" className="sr-only">Sermon editor</h3>
          <RichTextEditor ref={editorRef} initialContent={{ ops: [{ insert: '\n' }] }} fontSize={fontSize} onChange={setDraft} onEnter={handleEditorEnter} placeholder="Write your sermon content here..." />
          <p className="autosave-note">Autosaves every 30 seconds.</p>
        </section>

        <section className="blocks-section" aria-labelledby="blocks-title">
          <div className="blocks-header"><h3 id="blocks-title" className="blocks-title">Your Content</h3>{blocks.length > 0 && <button type="button" className="btn btn-link block-order-toggle" onClick={() => setReverse((value) => !value)}>{reverse ? 'Newest First ↓' : 'Oldest First ↑'}</button>}</div>
          {!blocks.length ? <p className="status-panel">No content yet. Start writing in the editor above.</p> : <div className="writer-blocks">
            {orderedBlocks.map((block) => (
              <article className="writer-block" key={block.block_id}>
                {editingBlockId === block.block_id ? <BlockEditor initialContent={block.content} onSave={(content) => saveEditedBlock(block.block_id, content)} onCancel={() => setEditingBlockId(null)} saving={saving} /> : <>
                  <DeltaRenderer content={block.content} />
                  <button type="button" className="btn btn-link edit-block-button" onClick={() => setEditingBlockId(block.block_id)}>Edit</button>
                </>}
              </article>
            ))}
          </div>}
        </section>
      </>}
    </div>
  );
}
